import React from "react";
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import DashboardPanelHeader from "components/DashboardPanelHeader";
import { enterpriseTokens, overviewChartPalette } from "styles/theme";

const SafetyAuditTraining = ({ data }) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const fiscalStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const elapsedFiscalMonths = currentMonth >= 3 ? currentMonth - 2 : currentMonth + 10;
    const expectedAttainment = (elapsedFiscalMonths / 12) * 100;
    const monthYear = now.toLocaleString("en-IN", { month: "short", year: "2-digit" });
    const financialYear = `${String(fiscalStartYear).slice(-2)}-${String(fiscalStartYear + 1).slice(-2)}`;

    const rows = [
        { label: "Workforce training", current: data?.totalEHSTrainingInCurrentMonth, ytd: data?.totalEHSTrainingTillDate, target: data?.totalEHSTrainingYearlyTarget },
        { label: "Site inspections", current: data?.totalSiteInspectionInCurrentMonth, ytd: data?.totalSiteInspectionTillDate, target: data?.totalSiteInspectionYearlyTarget },
        { label: "Third-party audits", current: data?.totalThirdPartyAuditInCurrentMonth, ytd: data?.totalThirdPartyAuditTillDate, target: data?.totalThirdPartyAuditYearlyTarget },
        { label: "Middle-management visits", current: data?.totalSafetyVisitByMiddleManagementInCurrentMonth, ytd: data?.totalSafetyVisitByMiddleManagementTillDate, target: data?.totalSafetyVisitByMiddleManagementYearlyTarget },
        { label: "Top-management visits", current: data?.totalSafetyVisitByTopManagementInCurrentMonth, ytd: data?.totalSafetyVisitByTopManagementTillDate, target: data?.totalSafetyVisitByTopManagementYearlyTarget },
    ].map((item) => {
        const current = Number(item.current) || 0;
        const ytd = Number(item.ytd) || 0;
        const target = Number(item.target) || 0;
        const attainment = target > 0 ? Math.min((ytd / target) * 100, 100) : 0;
        const onTrack = target > 0 && attainment >= expectedAttainment;
        return { ...item, current, ytd, target, attainment, onTrack };
    });

    return (
        <Box sx={{ border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: "3px", overflow: "hidden", backgroundColor: "#FFFFFF" }}>
            <DashboardPanelHeader
                eyebrow="Assurance scorecard"
                title="HSE Activity Attainment"
                subtitle={`Current month (${monthYear}), FY ${financialYear} progress, and annual target position.`}
            />

            <Box sx={{ px: 1.5, py: 1.25 }}>
                <Box
                    sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns: "minmax(220px, 1.6fr) repeat(3, minmax(90px, 0.65fr)) minmax(220px, 1.4fr)",
                        gap: 1.5,
                        px: 1.25,
                        pb: 1,
                        color: enterpriseTokens.muted,
                        fontSize: "10px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                    }}
                >
                    <Box>Activity</Box>
                    <Box>Current month</Box>
                    <Box>YTD</Box>
                    <Box>Annual target</Box>
                    <Box>Attainment</Box>
                </Box>

                <Box sx={{ display: "grid", gap: 0.75 }}>
                    {rows.map((item) => {
                        const statusColor = item.onTrack ? overviewChartPalette.teal : overviewChartPalette.coral;
                        return (
                            <Box
                                key={item.label}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "repeat(3, 1fr)", md: "minmax(220px, 1.6fr) repeat(3, minmax(90px, 0.65fr)) minmax(220px, 1.4fr)" },
                                    gap: { xs: 1, md: 1.5 },
                                    alignItems: "center",
                                    p: 1.25,
                                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                                    backgroundColor: "#FBFCFE",
                                }}
                            >
                                <Typography sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, fontSize: "12px", fontWeight: 650, color: enterpriseTokens.navy }}>
                                    {item.label}
                                </Typography>
                                {[
                                    ["Month", item.current],
                                    ["YTD", item.ytd],
                                    ["Target", item.target],
                                ].map(([label, value]) => (
                                    <Box key={label}>
                                        <Typography sx={{ display: { md: "none" }, fontSize: "9px", color: enterpriseTokens.muted, textTransform: "uppercase" }}>{label}</Typography>
                                        <Typography sx={{ fontSize: "17px", lineHeight: "22px", fontWeight: 700, color: enterpriseTokens.navy }}>{value}</Typography>
                                    </Box>
                                ))}
                                <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                                    <Box sx={{ mb: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: statusColor }}>{item.attainment.toFixed(0)}%</Typography>
                                        <Chip
                                            label={item.onTrack ? "On track" : "Attention"}
                                            size="small"
                                            sx={{ height: 18, borderRadius: "999px", color: statusColor, bgcolor: `${statusColor}12`, border: `1px solid ${statusColor}33`, fontSize: "8px", fontWeight: 700, textTransform: "uppercase" }}
                                        />
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={item.attainment}
                                        sx={{ height: 5, borderRadius: 0, backgroundColor: "#E2E8F0", "& .MuiLinearProgress-bar": { borderRadius: 0, backgroundColor: statusColor } }}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default SafetyAuditTraining;
